import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { xdrDroppedQuery, xdrHealthQuery, type XdrDroppedDevice } from '../api/securitee'
import './edr-dropped.css'

const count = (n: number) => n.toLocaleString('en-US')

/** "DC01: 3,200 over the events-per-minute limit, 40 with the queue full"; cu firma pe "All companies". */
export function droppedLine(d: XdrDroppedDevice, withCompany = false): string {
  const parts: string[] = []
  if (d.rate > 0) parts.push(`${count(d.rate)} over the events-per-minute limit`)
  if (d.queue > 0) parts.push(`${count(d.queue)} with the queue full`)
  const device = d.device_name || `Device #${d.device_id}`
  return `${withCompany && d.company_name ? `${device} (${d.company_name})` : device}: ${parts.join(', ')}`
}

/** '2026-10-22' -> 'Oct 22', fara fusul orar al browserului */
const dayLabel = (day: string) => new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })

/**
 * Aparatele care au aruncat evenimente peste plafoane in ultimele 24 de ore
 * (GET /securitee/xdr/health, `dropped`): banda galbena langa cea rosie. Fara
 * aparate, fara drept sau cu eroare: nimic - banda nu strica pagina.
 */
export function EdrDroppedAlert() {
  const q = useQuery(xdrHealthQuery())
  const [open, setOpen] = useState<number | null>(null)
  const devices = (q.data?.dropped ?? []).filter((d) => d.rate > 0 || d.queue > 0)
  if (devices.length === 0) return null
  // aparate din mai multe firme (coada "All companies"): numele singur nu ajunge
  const severalCompanies = new Set(devices.map((d) => d.company_id)).size > 1

  return (
    <div className="alert alert-warning edr-dropped-alert" role="status" style={{ marginBottom: 12 }}>
      <strong>Some devices dropped events in the last 24 hours.</strong>
      <ul style={{ margin: '4px 0', paddingLeft: 18 }}>
        {devices.map((d) => {
          const id = `edr-dropped-${d.device_id}`
          const isOpen = open === d.device_id
          return (
            <li key={`${d.company_id ?? 0}-${d.device_id}`}>
              <span className="edr-dropped-line">{droppedLine(d, severalCompanies)}</span>{' '}
              <button
                type="button"
                className="btn btn-link btn-xs edr-dropped-toggle"
                aria-expanded={isOpen}
                aria-controls={id}
                onClick={() => setOpen(isOpen ? null : d.device_id)}
              >
                {isOpen ? 'Hide details' : 'Details'}
              </button>
              {isOpen && <EdrDroppedDetail id={id} device={d.device_id} />}
            </li>
          )
        })}
      </ul>
      Events the detection rules use are kept first. To keep everything from these devices, raise Events per minute or
      Queue size in Settings &gt; Securitee &gt; XDR.
    </div>
  )
}

/** Ce jurnal s-a inundat si cand (GET /securitee/xdr/dropped/{id}), citit numai la "Details". */
function EdrDroppedDetail({ id, device }: { id: string; device: number }) {
  const q = useQuery(xdrDroppedQuery(device))
  if (q.isPending) return <div id={id} className="edr-dropped-detail text-muted">Loading…</div>
  if (q.isError) return <div id={id} className="edr-dropped-detail text-muted">Could not load the details.</div>
  const { channels, minutes, days } = q.data

  return (
    <div id={id} className="edr-dropped-detail">
      {channels.length === 0 && minutes.length === 0 && (
        <p className="edr-dropped-none">No per-minute counts yet: they come with the agent update that counts them.</p>
      )}
      {channels.length > 0 && (
        <div className="edr-dropped-channels">
          <strong>By log, last 24 hours:</strong> {channels.map((c) => `${c.channel} ${count(c.rate + c.queue)}`).join(' · ')}
        </div>
      )}
      {minutes.length > 0 && (
        <div className="edr-dropped-minutes">
          <table className="table table-condensed">
            <caption>Busiest minutes, last 7 days (UTC)</caption>
            <thead>
              <tr>
                {/* cifrele langa minut: pe telefon se vad fara derulare */}
                <th scope="col">Minute</th>
                <th scope="col">Over the limit</th>
                <th scope="col">Queue full</th>
                <th scope="col">Log</th>
                <th scope="col">
                  <span className="sr-only">Events</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {minutes.map((m) => (
                <tr key={`${m.minute}-${m.channel}`}>
                  <td>{m.minute.slice(0, 16)}</td>
                  <td>{count(m.rate)}</td>
                  <td>{count(m.queue)}</td>
                  <td>{m.channel}</td>
                  <td>
                    <a className="alert-link" data-spa href={`/app/securitee/edr-events?device=${device}&around=${m.minute.slice(0, 19).replace(' ', 'T')}`}>
                      EDR events around {m.minute.slice(11, 16)}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {days.length > 0 && (
        <div className="edr-dropped-days">
          <strong>Per day, last 7 days:</strong> {days.map((d) => `${dayLabel(d.day)} ${count(d.events)}`).join(' · ')}
        </div>
      )}
    </div>
  )
}
